import { useRef, useState } from 'react'
import Map, { Layer, MapRef, Marker, Source, Popup } from 'react-map-gl'
import bbox from '@turf/bbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useBoatStops } from '../api/devices'
import { BoatStop } from '../models/devices'

// Helper function to format duration in human-readable format
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`
  }

  const days = Math.floor(minutes / (24 * 60))
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const mins = minutes % 60

  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0) parts.push(`${mins}min`)

  return parts.join(' ') || '0 min'
}

type Props = {
  track: { coordinates: [number, number][]; type: 'LineString' }
  deviceImei: string
  startDate: Date
  endDate: Date
}

export default function TrackMapContainer({
  track,
  deviceImei,
  startDate,
  endDate,
}: Props) {
  const mapRef = useRef<MapRef>(null)
  const [selectedStop, setSelectedStop] = useState<BoatStop | null>(null)

  const {
    data: boatStopsData,
    error: stopsError,
    isLoading: stopsLoading,
  } = useBoatStops(deviceImei, startDate, endDate)

  // Debug logging
  console.log('Boat stops data:', boatStopsData)
  console.log('Stops error:', stopsError)
  console.log('Stops loading:', stopsLoading)

  const geojson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: track,
      },
    ],
  }
  console.log(geojson)

  const handleMapLoad = () => {
    if (mapRef.current && track.coordinates.length > 0) {
      if (track.coordinates.length === 1) {
        mapRef.current.flyTo({
          center: track.coordinates[0],
          zoom: 15,
          duration: 200,
        })
      } else {
        const [minLng, minLat, maxLng, maxLat] = bbox(geojson as any)
        const isTinyArea = minLng === maxLng || minLat === maxLat

        if (isTinyArea) {
          mapRef.current.flyTo({
            center: track.coordinates[0],
            zoom: 15,
            duration: 200,
          })
        } else {
          mapRef.current.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: 150,
              duration: 200,
              maxZoom: 13,
            }
          )
        }
      }
    }
  }

  return (
    <Map
      ref={mapRef}
      onLoad={handleMapLoad}
      mapboxAccessToken="pk.eyJ1IjoiaGJhbmRvdiIsImEiOiJjbHVoMTZqazEya2p5MnNsMHF1c3ZicmV6In0.wcM_OYGTxxDZq8D8tnfKxA"
      initialViewState={{
        latitude: track.coordinates[0][1],
        longitude: track.coordinates[0][0],
        zoom: 8,
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle="mapbox://styles/mapbox/streets-v9"
      attributionControl={false}
    >
      <Marker
        key="Start Point"
        longitude={track.coordinates[0][0]}
        latitude={track.coordinates[0][1]}
        style={{ cursor: 'pointer' }}
        color="green"
      />
      {track.coordinates.length > 1 && (
        <Marker
          key="End Point"
          longitude={track.coordinates[track.coordinates.length - 1][0]}
          latitude={track.coordinates[track.coordinates.length - 1][1]}
          style={{ cursor: 'pointer' }}
          color="red"
        />
      )}

      {/* Display boat stops */}
      {boatStopsData?.stops.map((stop) => (
        <Marker
          key={`stop-${stop.stop_id}`}
          longitude={stop.location.coordinates[0]}
          latitude={stop.location.coordinates[1]}
          color="orange"
          anchor="bottom"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.originalEvent.stopPropagation()
            console.log('Marker clicked!', stop)
            setSelectedStop(stop)
          }}
        />
      ))}

      {/* Popup for selected stop */}
      {selectedStop && (
        <Popup
          longitude={selectedStop.location.coordinates[0]}
          latitude={selectedStop.location.coordinates[1]}
          anchor="bottom"
          onClose={() => setSelectedStop(null)}
        >
          <div style={{ padding: '10px' }}>
            <h3 style={{ margin: '0 0 10px 0' }}>Boat Stop</h3>
            <p>
              <strong>Start:</strong>{' '}
              {new Date(selectedStop.start_time).toLocaleString()}
            </p>
            <p>
              <strong>End:</strong>{' '}
              {selectedStop.end_time
                ? new Date(selectedStop.end_time).toLocaleString()
                : 'Ongoing'}
            </p>
            {selectedStop.end_time && (
              <p>
                <strong>Duration:</strong>{' '}
                {formatDuration(
                  Math.round(
                    (new Date(selectedStop.end_time).getTime() -
                      new Date(selectedStop.start_time).getTime()) /
                      (1000 * 60)
                  )
                )}
              </p>
            )}
          </div>
        </Popup>
      )}

      <Source id="multiline-source" type="geojson" data={geojson}>
        <Layer
          id="multiline-layer"
          type="line"
          source="multiline-source"
          paint={{
            'line-color': 'red',
            'line-width': 2,
          }}
        />
      </Source>
    </Map>
  )
}
